'use client';

import { useRef, useCallback, useMemo, useEffect } from 'react';
import { useAtom } from 'jotai';
import SecondaryTabs from './components/secondary-tabs';
import { getBagItems, useGetBagTabs, clearBagRedDot, clearSingleBagRedDot } from '../../services/backpack';
import usePagination from '@/features/casino/hooks/use-pagination';
import { buildUIItem } from './adapter';
import type { UIItem } from './adapter';
import { track } from '@/utils/track';
import { backpackActiveTabAtom } from '../../store/backpack-tab';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/navigation';
import { menuTabUrl } from '@/const/page-url';
import useFn from '@/hooks/use-fn';
import { useBagCountManager } from '../../store/bag-count';

interface BackpackProps {
  onClose?: () => void;
}

const sourceUrlMap: Record<string, string | undefined> = {
  fantasy: menuTabUrl?.fantasy,
  rummy: menuTabUrl?.rummy,
};

export default function Backpack(props?: BackpackProps) {
  const t = useTranslations('reward');
  const router = useRouter();
  const [activeTab, setActiveTab] = useAtom(backpackActiveTabAtom);
  const activeTabRef = useRef(activeTab);
  const requestIdRef = useRef(0);
  const isInitializedRef = useRef(false);
  const { updateBagCount } = useBagCountManager();

  const { data: tabsData, isLoading } = useGetBagTabs.useQueryHook({});
  const tabs = useMemo(() => {
    if (isLoading) {
      return [];
    }
    const allItem = [
      {
        value: '',
        label: t('all'),
        label_en: 'All',
      }
    ];
    return allItem.concat(tabsData?.tabs?.map((tab) => ({
      value: tab.code,
      label: tab.title,
      label_en: tab?.title_en || tab.title || ''
    })) || []) || [];
  }, [tabsData, isLoading]);

  const loadItems = useCallback(
    async ({ current, pageSize }: { current: number; pageSize: number; }) => {
      const requestId = ++requestIdRef.current;
      const res = await getBagItems({
        tab_code: activeTabRef.current,
        page: String(current),
        page_size: String(pageSize),
      });

      if (requestId !== requestIdRef.current) {
        return {
          list: [],
          total: 0,
          hasMore: true,
          nextPage: 1,
        };
      }

      return {
        list: (res?.list ?? []).map(buildUIItem),
        total: res?.total,
        nextPage: current + 1,
      };
    },
    [],
  );

  const { list, loadMore, refresh, clearList, isFirstLoad, isEmpty, hasMore } =
    usePagination(loadItems, { autoRun: false });

  useEffect(() => {
    track('backpack_page_view');
  }, []);

  useEffect(() => {
    if (!tabs?.length || isInitializedRef.current) return;
    isInitializedRef.current = true;

    const tabValues = tabs.map((t) => t.value);
    // 上次有记录且 tab 仍在列表中则恢复，否则取第一个
    const targetTab = activeTab && tabValues.includes(activeTab) ? activeTab : tabs[0].value;

    activeTabRef.current = targetTab;
    setActiveTab(targetTab);
    track('backpack_tab_page_view', {
      current_page: tabs.find(t => t.value === targetTab)?.label_en ?? targetTab,
    });
    refresh();
  }, [tabs]);

  const handleTabChange = useFn((tab: string) => {
    activeTabRef.current = tab;
    setActiveTab(tab);
    track('backpack_tab_page_view', {
      current_page: tabs.find(t => t.value === tab)?.label_en ?? tab,
    });
    clearList();
    refresh();
    clearBagRedDot({ tab_code: tab }).then(() => {
      updateBagCount();
    });
  });

  const propStateMap: Record<UIItem['uiStatus'], number> = {
    ACTIVE: 1,
    EXPIRING: 1,
    USED: 2,
    NOT_STARTED: 3,
    EXPIRED: 4,
  };

  const buildPropTrackParams = (item: UIItem, buttonName: string) => ({
    current_page: tabs.find(t => t.value === activeTab)?.label_en ?? activeTab,
    prop_id: item.item_id,
    // 需要英文名称
    prop_name: item.prop_name,
    // 1待使用 2已使用 3不可使用 4已过期
    prop_state: propStateMap[item.uiStatus],
    // 获取时间
    obtain_time: item.grant_at,
    valid_end_time: item.expire_at,
    // 最近获取或刷新时间，前端上报使用
    use_time: item.effective_at,
    button_name: buttonName,
  });

  const handleUse = (item: UIItem) => {
    track('prop_button_click', {
      ...buildPropTrackParams(item, 'use'),
    });

    clearSingleBagRedDot({ item_id: item.item_id, tab_code: activeTab }).then(() => {
      updateBagCount();
    });

    // 构建URL参数
    const params = new URLSearchParams();

    // 处理target参数
    if (item?.target && Array.isArray(item.target)) {
      item.target.forEach((targetItem) => {
        if (targetItem.target_field && targetItem.target_id) {
          params.append(targetItem.target_field, targetItem.target_id);
        }
      });
    }

    const targetPath = sourceUrlMap[item?.source ?? ''];
    if (targetPath) {
      const paramsString = params.toString();
      const url = paramsString ? `${targetPath}?${paramsString}` : targetPath;
      router.push(url);
    }
    // 跳转关闭弹窗
    props?.onClose?.();
  };

  return (
    <SecondaryTabs
      value={activeTab}
      onValueChange={handleTabChange}
      tabs={tabs}
      items={list}
      isFirstLoad={isFirstLoad}
      isEmpty={isEmpty}
      hasMore={hasMore}
      onLoadMore={loadMore}
      onUse={handleUse}
    />
  );
}

